import { getGrid } from 'api/gridApi';
import type { ReactNode } from 'react';
import { normalizeProjectCoordinatesForMap } from 'utils/coordinateSystemManager';
import { lv95ToWgs84ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';
import { type GeodataTarget } from 'contexts/GeodataContext';
import { getTargetRadiusMax } from 'constants/geodata';
import { firstPolygon, normalizeMultiPolygon } from 'utils/multiPolygonHelper';
import { type ProjectCreationPreviewState } from 'views/project-creation/ProjectCreationPersistentPreview';

export interface AddSimulationProps {
  reviewMode?: boolean;
  reviewSimulationId?: string | null;
  breadcrumbTitle?: ReactNode;
  geodataIdOverride?: string | null;
  gridIdOverride?: string | null;
  projectIdOverride?: string | null;
  simulationSetIdOverride?: string | null;
  onExitOverride?: () => void;
  onBackOverride?: () => void;
  disableRun?: boolean;
  isVolumeUsageLoading?: boolean;
  onConfirmVolumeUsage?: (run: () => Promise<void>) => void | Promise<void>;
  previewLoading?: boolean;
  onUpdatePreview?: (gridId: string) => void | Promise<void>;
  onPreviewStateChange?: (preview: ProjectCreationPreviewState | null) => void;
  onPreviewSlotRectChange?: (rect: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'> | null) => void;
  previewRectAnchor?: HTMLElement | null;
}

export interface SimulationGeodata {
  id?: number | string | null;
  name?: string | null;
  description?: string | null;
  gridId?: number | string | null;
  progressStatus?: string | null;
  hasBuilding?: boolean | null;
  hasVegetation?: boolean | null;
  building?: unknown;
  vegetation?: unknown;
  terrain?: Record<string, unknown> | null;
  params?: { targets?: unknown; [key: string]: unknown };
  [key: string]: unknown;
}

export type GridCoordinate = {
  x: number;
  y: number;
  z: number;
};

export type SimulationGrid = Awaited<ReturnType<typeof getGrid>>;
export type SimulationGridView = Omit<SimulationGrid, 'params'> & {
  params?:
    | (Omit<NonNullable<SimulationGrid['params']>, 'analysisArea'> & {
        analysisArea?: GridCoordinate[];
        analysisAreas?: GridCoordinate[][];
      })
    | null;
};

export type SimulationProjectData = {
  id?: string | number;
  coordinates?: { lat?: number | null; lng?: number | null } | null;
  projectCoordinates?: unknown;
  [key: string]: unknown;
};

export const getTargetViewDefinitions = (lakeRadius: number) =>
  [
    { type: 'LAKE', name: 'Lake View', defaultRadius: lakeRadius, defaultEnabled: true },
    { type: 'RIVER', name: 'River View', defaultRadius: 1000, defaultEnabled: true },
    { type: 'FOREST', name: 'Forest View', defaultRadius: 2500, defaultEnabled: false },
  ] as const;

export const toFiniteNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const buildStandardTargets = (
  availability: { hasLake: boolean; hasRiver: boolean },
  currentTargets: GeodataTarget[],
  minArea: unknown,
  lakeRadius: number
) => {
  const customTargets = currentTargets.filter((target) => target?.type === 'CUSTOM');

  return [
    ...(availability.hasLake
      ? [{ id: 1, type: 'LAKE', name: 'Lakes', radius: lakeRadius, minArea: null, polygon: null }]
      : []),
    ...(availability.hasRiver
      ? [{ id: 2, type: 'RIVER', name: 'Rivers', radius: 1000, minArea: null, polygon: null }]
      : []),
    {
      id: 3,
      type: 'FOREST',
      name: 'Forests',
      radius: 2500,
      minArea: Number.isFinite(Number(minArea)) ? Number(minArea) : 1000,
      polygon: null,
    },
    ...customTargets,
  ] as GeodataTarget[];
};

export const getTargetsSignature = (targets: GeodataTarget[]) =>
  JSON.stringify(
    targets.map((target) => ({
      id: target?.id ?? null,
      type: target?.type ?? null,
      name: target?.name ?? null,
      radius: target?.radius ?? null,
      minArea: target?.minArea ?? null,
      polygon: target?.polygon ?? null,
    }))
  );

export const resolveTargetDetectionArea = (
  gridData: { params?: Record<string, unknown> | null } | null | undefined,
  geodataLengthFar: unknown,
  geodataLengthMid: unknown
) => {
  const params = (gridData?.params ?? {}) as Record<string, unknown>;
  const manualCenter = (params.manualCenter ?? null) as { x?: unknown; y?: unknown } | null;
  const analysisArea = Array.isArray(params.analysisArea)
    ? (params.analysisArea as Array<{ x?: unknown; y?: unknown }>)
    : [];
  const radiusMeters = getTargetRadiusMax(Number(geodataLengthMid), Number(geodataLengthFar));

  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) {
    return null;
  }

  const manualCenterX = toFiniteNumber(manualCenter?.x);
  const manualCenterY = toFiniteNumber(manualCenter?.y);

  if (manualCenterX != null && manualCenterY != null) {
    return {
      center: lv95ToWgs84ForOneCoordinate(manualCenterX, manualCenterY),
      radiusMeters,
    };
  }

  const uniqueAnalysisArea = analysisArea.slice(
    0,
    analysisArea.length > 1 &&
      Number(analysisArea[0]?.x) === Number(analysisArea[analysisArea.length - 1]?.x) &&
      Number(analysisArea[0]?.y) === Number(analysisArea[analysisArea.length - 1]?.y)
      ? -1
      : analysisArea.length
  );

  const finiteCoordinates = uniqueAnalysisArea
    .map((coordinate) => ({
      x: toFiniteNumber(coordinate?.x),
      y: toFiniteNumber(coordinate?.y),
    }))
    .filter((coordinate): coordinate is { x: number; y: number } => coordinate.x != null && coordinate.y != null);

  if (finiteCoordinates.length === 0) {
    return null;
  }

  const centerX = finiteCoordinates.reduce((sum, coordinate) => sum + coordinate.x, 0) / finiteCoordinates.length;
  const centerY = finiteCoordinates.reduce((sum, coordinate) => sum + coordinate.y, 0) / finiteCoordinates.length;

  return {
    center: lv95ToWgs84ForOneCoordinate(centerX, centerY),
    radiusMeters,
  };
};

export const normalizeProjectForMap = (
  projectData: Record<string, unknown> | null | undefined
): SimulationProjectData | null => {
  if (!projectData) {
    return null;
  }

  return normalizeProjectCoordinatesForMap({
    ...projectData,
    coordinates: ((projectData as { projectCoordinates?: unknown }).projectCoordinates ??
      (projectData as { coordinates?: unknown }).coordinates ??
      undefined) as SimulationProjectData['coordinates'],
  } as SimulationProjectData) as SimulationProjectData | null;
};

export const normalizeGridForSimulationView = (gridData: SimulationGrid): SimulationGridView => {
  if (!gridData?.params) {
    return gridData as SimulationGridView;
  }

  const gridParams = gridData.params as NonNullable<SimulationGrid['params']> & {
    analysisAreas?: GridCoordinate[][] | null;
  };
  const analysisAreas = normalizeMultiPolygon<GridCoordinate>(gridParams.analysisAreas ?? gridParams.analysisArea);

  return {
    ...gridData,
    params: {
      ...gridParams,
      legalBorder: firstPolygon(gridParams.legalBorder),
      buildableBorder: firstPolygon(gridParams.buildableBorder),
      analysisArea: analysisAreas[0] ?? firstPolygon(gridParams.analysisArea),
      analysisAreas,
    },
  } as SimulationGridView;
};
